import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME } from '@/auth/routes'
import { MIN_PASSWORD } from '@/lib/password'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { PasswordField } from '@/ui/PasswordField'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'
import { useToken } from './useToken'

/**
 * `/reset-password` — the link `password_reset_link()` builds.
 *
 * The one token page that takes input, and the one that **lands the user signed
 * in**: the reset response sets the session cookie, which is what S-D2 screen
 * 09's "Set password & log in" promises. It is also why the token is spent on
 * submit rather than on load — there is nothing to redeem until there is a new
 * password to redeem it with.
 *
 * Setting a password revokes every other session, including any an attacker
 * holds. That is stated on the screen, because it is the reason a reset is
 * worth doing after a compromise rather than a courtesy note.
 */
export function ResetPassword(): JSX.Element {
  const token = useToken()
  const navigate = useNavigate()
  const { adopt } = useAuth()

  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [mismatch, setMismatch] = useState<string | null>(null)

  const reset = useMutation((body: { token: string; password: string }) =>
    call(() => api.POST('/api/v1/auth/password/reset', { body })),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (!token) return
    if (password !== confirmation) {
      // Checked here rather than server-side because the server is never sent
      // the second field: a typo confirmation exists to catch a typo, and
      // posting both would make the API carry a rule that belongs to the form.
      setMismatch('The two passwords are different.')
      return
    }
    setMismatch(null)
    const result = await reset.run({ token, password })
    if (result.ok) {
      adopt({ user: result.data.user, session: result.data.session })
      void navigate(DEFAULT_HOME, { replace: true })
    }
  }

  if (!token) {
    return (
      <PublicPage title="That link is incomplete">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            The address in your browser has no reset code in it. Ask for a new link and open the
            newest message.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/forgot-password">
            Send a new link
          </Link>
        </div>
      </PublicPage>
    )
  }

  return (
    <PublicPage
      title="Set a new password"
      footer={
        <>
          Remembered it after all? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__body ar-stack">
          <InlineError failure={reset.state.failure} />
          <PasswordField
            label="New password"
            required
            autoFocus
            minLength={MIN_PASSWORD}
            autoComplete="new-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
              setMismatch(null)
              reset.reset()
            }}
          />
          <TextField
            label="Type it again"
            type="password"
            required
            autoComplete="new-password"
            error={mismatch ?? undefined}
            value={confirmation}
            onChange={(event) => {
              setConfirmation(event.target.value)
              setMismatch(null)
            }}
          />
          <p className="ar-field__help" style={{ margin: 0 }}>
            Saving this signs you out everywhere else — every other browser and phone will need the
            new password.
          </p>
        </div>
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button tone="primary" type="submit" busy={reset.pending} busyLabel="Setting password">
            Set password &amp; log in
          </Button>
        </div>
      </form>
    </PublicPage>
  )
}
